import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Download,
  BarChart3,
  Search,
  UserCheck,
  UserX,
  Clock,
  FileText
} from 'lucide-react';
import { subscriberAPI, operatorAPI, formatNumber } from '../services/api';
import PDFExporter from '../utils/pdfExporter';
import { buildCsvRaw, downloadCsv } from '../utils/csvExport';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const CURRENT_YEAR = new Date().getFullYear();

const ReportSubscribers = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [operators, setOperators] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, suspended: 0, pending: 0 });
  const [byOperator, setByOperator] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [month, setMonth] = useState(DEFAULT_REPORT_MONTH);
  const [year, setYear] = useState(DEFAULT_REPORT_YEAR);
  const [operatorFilter, setOperatorFilter] = useState('all');
  const [appliedPeriod, setAppliedPeriod] = useState('monthly');
  const [appliedMonth, setAppliedMonth] = useState(DEFAULT_REPORT_MONTH);
  const [appliedYear, setAppliedYear] = useState(DEFAULT_REPORT_YEAR);
  const [appliedOperator, setAppliedOperator] = useState('all');
  const [isExporting, setIsExporting] = useState(false);

  const [report, setReport] = useState([]);

  useEffect(() => {
    operatorAPI.getAll()
      .then(res => setOperators(res?.data || []))
      .catch(() => setOperators([]));
  }, []);

  const applyFilters = () => {
    setAppliedPeriod(period);
    setAppliedMonth(month);
    setAppliedYear(year);
    setAppliedOperator(operatorFilter);
  };

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await subscriberAPI.report({ month, year, operators: operatorFilter });
      setReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      setError(error?.message ?? t('reportSubscribers.loadError'));
      setReport([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    // fetchData();
    fetchReport();
    // console.log({appliedMonth, appliedYear, appliedPeriod, appliedOperator});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when applied filters change only
  }, [month, year, operatorFilter]);

  // const PAGE_SIZE = 1000;

  // const fetchData = async () => {
  //   try {
  //     setLoading(true);
  //     setError(null);

  //     const opsRes = operators.length ? Promise.resolve({ data: operators }) : operatorAPI.getAll();
  //     const ops = (await opsRes)?.data || [];
  //     if (ops.length && !operators.length) setOperators(ops);

  //     const baseParams = {
  //       limit: PAGE_SIZE,
  //       ...(appliedOperator !== 'all' && { operator: appliedOperator }),
  //       ...(appliedPeriod === 'monthly' && { month: appliedMonth, year: appliedYear })
  //     };

  //     // Fetch all pages to collect every subscriber (no pagination cut-off for statistics)
  //     const allSubs = [];
  //     let page = 1;
  //     let hasMore = true;
  //     while (hasMore) {
  //       const res = await subscriberAPI.getAll({ ...baseParams, page });
  //       const subs = res?.data?.subscribers || [];
  //       allSubs.push(...subs);
  //       hasMore = subs.length >= PAGE_SIZE;
  //       page += 1;
  //     }
  //     const subs = allSubs;

  //     // 1) Total subscribers, separated by status (derived from full collected list)
  //     let total = subs.length;
  //     let active = 0;
  //     let suspended = 0;
  //     let pending = 0;
  //     subs.forEach(s => {
  //       const st = (s.status ?? '').toLowerCase();
  //       if (st === 'active') active += 1;
  //       else if (st === 'suspended') suspended += 1;
  //       else pending += 1;
  //     });
  //     setSummary({ total, active, suspended, pending });

  //     // 2) Summary by operator: aggregate total and status counts per operator
  //     const operatorMap = new Map();
  //     const resolveOperatorName = (opId) => {
  //       const op = ops.find(o => String(o.operator_id) === String(opId) || String(o.id) === String(opId));
  //       return op ? (op.operator_name ?? op.name ?? String(opId)) : (opId === 'unknown' ? 'Unknown' : String(opId));
  //     };
  //     subs.forEach(s => {
  //       const opId = s.operator_id ?? s.operatorId ?? 'unknown';
  //       const key = String(opId);
  //       if (!operatorMap.has(key)) {
  //         operatorMap.set(key, {
  //           operator_id: key,
  //           operator_name: resolveOperatorName(key),
  //           total: 0,
  //           active: 0,
  //           suspended: 0,
  //           pending: 0
  //         });
  //       }
  //       const rec = operatorMap.get(key);
  //       rec.total += 1;
  //       const st = (s.status ?? '').toLowerCase();
  //       if (st === 'active') rec.active += 1;
  //       else if (st === 'suspended') rec.suspended += 1;
  //       else rec.pending += 1;
  //     });

  //     const grandTotal = [...operatorMap.values()].reduce((s, r) => s + r.total, 0);
  //     const rows = [...operatorMap.values()]
  //       .map(r => ({
  //         ...r,
  //         sharePct: grandTotal > 0 ? (r.total / grandTotal) * 100 : 0
  //       }))
  //       .sort((a, b) => b.total - a.total);

  //     setByOperator(rows);
  //   } catch (err) {
  //     console.error(err);
  //     setError(err?.message ?? 'Failed to load subscriber report');
  //     setSummary({ total: 0, active: 0, suspended: 0, pending: 0 });
  //     setByOperator([]);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const reportPeriodLabel = () => {
    if (appliedPeriod === 'yearly') return `${appliedYear}`;
    if (appliedPeriod === 'quarterly') {
      const q = Math.ceil(appliedMonth / 3);
      return `Q${q} ${appliedYear}`;
    }
    return `${t(`common.monthNames.${appliedMonth}`, { defaultValue: appliedMonth })} ${appliedYear}`;
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    const filename = `subscribers-report-${appliedPeriod}-${reportPeriodLabel().replace(/\s/g, '-')}`;
    const result = await PDFExporter.exportToPDF('report-subscribers', filename, { scale: 1.5 });
    if (!result.success) alert(t('common.exportPdfFailed') + ': ' + result.error);
    setIsExporting(false);
  };

  const handleExportCSV = () => {
    const periodLabel = reportPeriodLabel();
    const generated = new Date().toISOString().slice(0, 10);
    const rows = [
      ['Report', 'Subscribers Report'],
      ['Period', periodLabel],
      ['Generated', generated],
      [],
      ['Total Subscribers', 'Active', 'Suspended', 'Pending'],
      [summary.total, summary.active, summary.suspended, summary.pending],
      [],
      ['Operator', 'Total', 'Share (%)', 'Active', 'Suspended', 'Pending'],
      ...byOperator.map(r => [r.operator_name, r.total, r.sharePct.toFixed(1), r.active, r.suspended, r.pending]),
      ...(byOperator.length > 0 ? [['Total', summary.total, '100', summary.active, summary.suspended, summary.pending]] : [])
    ];
    const csv = buildCsvRaw(rows);
    const filename = `subscribers-report-${periodLabel.replace(/\s/g, '-')}`;
    downloadCsv(filename, csv);
  };

  return (
    <div id="report-subscribers" className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Official report header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 print:shadow-none">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{t('reportSubscribers.title')}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{t('common.appBrand')}</p>
              <p className="text-sm text-gray-600 mt-2">
                {t('common.reportPeriod')}: <strong>{reportPeriodLabel()}</strong>
                {appliedOperator !== 'all' && (
                  <> · {t('common.operator')}: <strong>{operators.find(o => String(o.operator_id) === appliedOperator)?.operator_name ?? appliedOperator}</strong></>
                )}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('common.generated')}: {new Date().toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExportCSV}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <FileText className="h-4 w-4" />
                {t('common.exportCSV')}
              </button>
              <button
                type="button"
                onClick={handleExportPDF}
                disabled={isExporting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <Download className="h-4 w-4" />
                {isExporting ? t('common.exporting') : t('common.exportPDF')}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.period')}</label>
                <select
                  value={period}
                  onChange={e => setPeriod(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="monthly">{t('common.monthly')}</option>
                  <option value="quarterly">{t('common.quarterly')}</option>
                  <option value="yearly">{t('common.yearly')}</option>
                </select>
              </div>
              {period === 'monthly' && (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.month')}</label>
                    <select
                      value={month}
                      onChange={e => setMonth(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {MONTHS.map(m => (
                        <option key={m.value} value={m.value}>{t(`common.monthNames.${m.value}`)}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.year')}</label>
                    <select
                      value={year}
                      onChange={e => setYear(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i).map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {(period === 'quarterly' || period === 'yearly') && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.year')}</label>
                  <select
                    value={year}
                    onChange={e => setYear(Number(e.target.value))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i).map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{t('common.operator')}</label>
                <select
                  value={operatorFilter}
                  onChange={e => setOperatorFilter(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">{t('common.allOperators')}</option>
                  {operators.map(o => (
                    <option key={o.operator_id} value={o.operator_id}>{o.operator_name ?? o.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <button
              type="button"
              onClick={applyFilters}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <Search className="h-4 w-4" />
              {t('common.apply')}
            </button>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            {t('common.reportPeriod')}: <strong className="text-gray-700">{reportPeriodLabel()}</strong>
            {appliedOperator !== 'all' && (
              <> · {t('common.operator')}: <strong className="text-gray-700">{operators.find(o => String(o.operator_id) === appliedOperator)?.operator_name ?? appliedOperator}</strong></>
            )}
          </p>
        </div>

        {/* {JSON.stringify(operators, null, 2)} */}


        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 text-red-800">
            <span className="text-red-500 text-lg">{t('common.failedToLoad')}</span>
            <span className="text-sm">{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border border-gray-200">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4" />
            <p className="text-gray-600">{t('reportSubscribers.loading')}</p>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-primary-100 rounded-lg">
                    <Users className="h-6 w-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.totalSubscribers')}</p>
                    <p className="text-2xl font-bold text-gray-900 tabular-nums">{formatNumber(report?.summary?.total_subscribers ?? 0)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-emerald-100 rounded-lg">
                    <UserCheck className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.active')}</p>
                    <p className="text-2xl font-bold text-emerald-700 tabular-nums">{formatNumber(report?.summary?.active_subscribers ?? 0)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-red-100 rounded-lg">
                    <UserX className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.suspended')}</p>
                    <p className="text-2xl font-bold text-red-700 tabular-nums">{formatNumber(report?.summary?.suspend_subscribers ?? 0)}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-100 rounded-lg">
                    <Clock className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">{t('common.pending')}</p>
                    <p className="text-2xl font-bold text-amber-700 tabular-nums">{formatNumber(report?.summary?.deactive_subscribers ?? 0)}</p>
                  </div>
                </div>
              </div>
            </div>


            {/* By operator table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">{t('reportSubscribers.summaryByOperator')}</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.operator')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.total')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.share')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.active')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.suspended')}</th>
                      <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.pending')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report?.by_operators?.map(row => {
                      const operator_name = operators.find(o => o.operator_id === row.operator_id)?.operator_name ?? row.operator_id;
                      const share_percentage = row.total_subscribers > 0 ? (row.total_subscribers / report?.summary?.total_subscribers) * 100 : 0;
                      return (
                        <tr key={row.operator_id} className="hover:bg-gray-50">
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{operator_name}</td>
                          <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(row.total_subscribers)}</td>
                          <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-500">{share_percentage.toFixed(1)}%</td>
                          <td className="px-5 py-3 text-sm text-right tabular-nums text-emerald-700">{formatNumber(row.active_subscribers)}</td>
                          <td className="px-5 py-3 text-sm text-right tabular-nums text-red-700">{formatNumber(row.suspend_subscribers)}</td>
                          <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatNumber(row.deactive_subscribers)}</td>
                        </tr>
                      )
                    })}
                    {report?.summary?.total_subscribers > 0 && (
                      <tr className="bg-gray-50 font-semibold border-t-2 border-gray-200">
                        <td className="px-5 py-3 text-sm text-gray-900">{t('common.total')}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-900">{formatNumber(report?.summary?.total_subscribers)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-gray-600">100%</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-emerald-700">{formatNumber(report?.summary?.active_subscribers)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-red-700">{formatNumber(report?.summary?.suspend_subscribers)}</td>
                        <td className="px-5 py-3 text-sm text-right tabular-nums text-amber-700">{formatNumber(report?.summary?.deactive_subscribers)}</td>
                      </tr>
                    )}
                    {report?.by_operators?.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-500">
                          {t('reportSubscribers.noData')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReportSubscribers;
